import {_utils, viewportChecker} from "../../utils/_utils.js";
import {_state} from "../../state/state.js";
import {initCardsSlider} from "../../utils/cards-slider.js";

/**
 * About — "Our team" (TZ section 6) / Team page.
 * Mobile/tablet (< 1025): cards carousel with arrows (utils/cards-slider.js, gap 32 as in the mobile design).
 * Desktop: plain 3-column CSS grid → the swiper is destroyed (styles + listeners removed).
 * Rebuilt only when crossing the desktop breakpoint (resizePage fires on any page-height change).
 * Optional group tabs (Team page): each panel (.ab-team__body) has its own carousel; a tab shows its panel,
 * ←/→/Home/End move between tabs.
 */
export const abTeam = () => {
    return new Promise((resolve) => {
        _utils.initSection('.ab-team', (section) => init(section));
        resolve();
    });
};

const init = (section) => {
    const panels = [...section.querySelectorAll('.ab-team__body')];
    if (!panels.length) return;

    const swipers = panels.map(() => null);

    const create = () => {
        panels.forEach((panel, i) => {
            if (swipers[i] !== null) {
                swipers[i].destroy(true, true);
                swipers[i] = null;
            }

            const slider = panel.querySelector('.ab-team__slider');
            if (!_state.isDesktop && slider) {
                swipers[i] = initCardsSlider(
                    slider,
                    panel.querySelector('.ab-team__nav-btn--prev'),
                    panel.querySelector('.ab-team__nav-btn--next'),
                    {spaceBetween: 32}
                );
            }
        });
    };

    create();
    initTabs(section, panels, swipers);

    let wasDesktop = _state.isDesktop;
    _utils.rebuildComponentsOnResize(() => {
        viewportChecker.all(); // updates _state.isDesktop
        if (_state.isDesktop === wasDesktop) return;
        wasDesktop = _state.isDesktop;
        create();
    });
};

const initTabs = (section, panels, swipers) => {
    const tablist = section.querySelector('.ab-team__tabs');
    const tabs = [...section.querySelectorAll('.ab-team__tab')];
    if (!tablist || tabs.length < 2) return;

    const activate = (index, focus = false) => {
        tabs.forEach((tab, i) => {
            const isActive = i === index;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.tabIndex = isActive ? 0 : -1;
        });
        panels.forEach((panel, i) => {
            const isActive = i === index;
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;
        });

        // a carousel created while its panel was hidden has no size yet
        if (swipers[index]) {
            swipers[index].update();
            swipers[index].slideTo(0, 0);
        }

        const tab = tabs[index];
        if (focus) tab.focus({preventScroll: true});

        // keep the chosen tab visible in the scrollable row (mobile)
        if (tablist.scrollWidth > tablist.clientWidth) {
            tablist.scrollTo({left: tab.offsetLeft - (tablist.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth'});
        }
    };

    tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
            if (!tab.classList.contains('is-active')) activate(i);
        });
    });

    tablist.addEventListener('keydown', (e) => {
        const current = tabs.findIndex((tab) => tab.classList.contains('is-active'));
        const keys = {
            ArrowRight: (current + 1) % tabs.length,
            ArrowLeft: (current - 1 + tabs.length) % tabs.length,
            Home: 0,
            End: tabs.length - 1,
        };
        if (!(e.key in keys)) return;
        e.preventDefault();
        activate(keys[e.key], true);
    });
};
